import { useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import styled from "styled-components";
import { Modal } from "../../../components/Modal";
import { Button } from "../../../components/Button";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { Input } from "../../../components/Input";
import { MoneyInput } from "../../../components/MoneyInput";
import { Toggle } from "../../../components/Toggle";
import { SelectInput } from "../../../components/SelectInput";
import { InfoBox } from "../../../components/InfoBox";
import { BillingItemEndButton } from "./BillingItemEndButton";
import { useIsAdmin } from "../../../hooks/useIsAdmin";
import { useGetStoreQuery } from "../../../query/storesQuery";
import {
  useSetBillingItemQuantityMutation,
  useUpdateBillingItemMutation,
} from "../../../query/billingItemsQuery";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { formatDate, toDateString } from "../../../utils/dateUtils";
import {
  dayBefore,
  getActiveSinceOnDate,
  getFirstSegmentStartDate,
  getQuantityOnDate,
  getQuantityTimeline,
  withSyncedSegmentDates,
} from "../../../utils/billingItemSegments";
import {
  getMinimumActiveUntilDate,
  getNonAdminMinimumEndDate,
  isDateBeforeMinimumActiveUntil,
  isEndDateBeforePaidUntil,
} from "../../../utils/billingItemMinimumActive";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import type { Money } from "@KassaroAB/shared/types/money";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { RestUpdateBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { BillingItemSegment } from "@KassaroAB/shared/types/billingItem";
import type { AvailableBillingItemId, StoreId } from "@KassaroAB/shared/types/ids";

const EditorFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const EditorRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const NumberInput = styled(Input)`
  width: 120px;
`;

const DateInput = styled(Input)`
  width: 180px;
`;

const PreviewBox = styled.div`
  margin-top: ${spacing.x1}px;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background-color: ${colors.background};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const PreviewTitle = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: ${spacing.x05}px;
`;

const TimelineList = styled.ul`
  margin: ${spacing.x2}px 0 0 0;
  padding-left: ${spacing.x3}px;
  list-style-type: disc;
`;

const TimelineEntry = styled.li`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin: ${spacing.x05}px 0;
`;

const DateLockedMessage = styled.p`
  margin: 0;
  color: ${colors.textMuted};
  font-size: ${fontSizes.regular}px;
`;

const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const SegmentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background-color: ${colors.background};
`;

const SegmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const SegmentTitle = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.text};
`;

const parseDateString = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toISODate() ?? "" : "";
};

const toDiscountAmountMajor = (discount: Money | null | undefined): number =>
  discount?.amount ? discount.amount / 100 : 0;

const toDiscountMoney = (
  amountMajor: number,
  currency: Currency
): Money | null => {
  const amountMinor = Math.round((amountMajor || 0) * 100);
  if (amountMinor <= 0) {
    return null;
  }
  return { amount: amountMinor, currency };
};

interface EditableSegment {
  startDate: string;
  quantity: number;
  billingPeriod: BillingPeriod;
  paidUntil: string;
  discountAmount: number;
}

const toEditableSegments = (
  billingItem: RestBillingItem,
  fallbackQuantity: number
): EditableSegment[] => {
  if (billingItem.segments && billingItem.segments.length > 0) {
    return billingItem.segments.map((segment) => ({
      startDate: parseDateString(segment.startDate),
      quantity: segment.quantity,
      billingPeriod:
        segment.billingPeriod ??
        billingItem.billingPeriod ??
        BillingPeriod.MONTHLY,
      paidUntil: parseDateString(segment.paidUntil),
      discountAmount: toDiscountAmountMajor(
        segment.discount ?? billingItem.discount
      ),
    }));
  }
  return [
    {
      startDate: parseDateString(billingItem.startDate),
      quantity: fallbackQuantity,
      billingPeriod: billingItem.billingPeriod ?? BillingPeriod.MONTHLY,
      paidUntil: parseDateString(billingItem.paidUntil),
      discountAmount: toDiscountAmountMajor(billingItem.discount),
    },
  ];
};

const toApiSegments = (
  segments: EditableSegment[],
  currency: Currency
): BillingItemSegment[] =>
  [...segments]
    .sort((first, second) => first.startDate.localeCompare(second.startDate))
    .map((segment) => ({
      startDate: segment.startDate,
      quantity: segment.quantity,
      billingPeriod: segment.billingPeriod,
      paidUntil: segment.paidUntil || null,
      discount: toDiscountMoney(segment.discountAmount, currency),
    }));

interface QuantityBillingItemEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  billingItems: RestBillingItem[] | undefined;
  billingItem: RestBillingItem | null;
  availableBillingItemId: AvailableBillingItemId;
  quantityLabel: string;
  defaultEffectiveFrom: string;
  quantityOffset?: number;
  minDisplayQuantity?: number;
  infoMessage?: string;
  previewTitle?: string;
  renderPreview?: (billingQuantity: number) => ReactNode;
  formatTimelineQuantity?: (quantity: number) => number;
  timelineLabel?: string;
}

export const QuantityBillingItemEditModal = ({
  isOpen,
  onClose,
  title,
  billingItems,
  billingItem,
  availableBillingItemId,
  quantityLabel,
  defaultEffectiveFrom,
  quantityOffset = 0,
  minDisplayQuantity = 0,
  infoMessage,
  previewTitle,
  renderPreview,
  formatTimelineQuantity = (quantity) => quantity,
  timelineLabel,
}: QuantityBillingItemEditModalProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <QuantityBillingItemEditModalContent
      onClose={onClose}
      title={title}
      billingItems={billingItems}
      billingItem={billingItem}
      availableBillingItemId={availableBillingItemId}
      quantityLabel={quantityLabel}
      defaultEffectiveFrom={defaultEffectiveFrom}
      quantityOffset={quantityOffset}
      minDisplayQuantity={minDisplayQuantity}
      infoMessage={infoMessage}
      previewTitle={previewTitle}
      renderPreview={renderPreview}
      formatTimelineQuantity={formatTimelineQuantity}
      timelineLabel={timelineLabel}
    />
  );
};

const QuantityBillingItemEditModalContent = ({
  onClose,
  title,
  billingItems,
  billingItem,
  availableBillingItemId,
  quantityLabel,
  defaultEffectiveFrom,
  quantityOffset = 0,
  minDisplayQuantity = 0,
  infoMessage,
  previewTitle,
  renderPreview,
  formatTimelineQuantity = (quantity) => quantity,
  timelineLabel,
}: Omit<QuantityBillingItemEditModalProps, "isOpen">) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { data: store } = useGetStoreQuery(storeId);
  const storeCurrency = store?.currency || Currency.SEK;
  const isAdmin = useIsAdmin();
  const today = toDateString(new Date());
  const todayIso = DateTime.now().startOf("day").toISODate() ?? today;
  const initialQuantity = getQuantityOnDate(
    billingItems ?? [],
    availableBillingItemId,
    today
  );
  const startDateDt = billingItem?.startDate
    ? DateTime.fromISO(billingItem.startDate).startOf("day")
    : null;
  const startDateHasStarted = !!startDateDt && startDateDt <= DateTime.now().startOf("day");
  const canEditStartDateAndPeriod = isAdmin || !startDateHasStarted;

  const [displayQuantity, setDisplayQuantity] = useState(
    Math.max(minDisplayQuantity, initialQuantity + quantityOffset)
  );
  const [effectiveFrom, setEffectiveFrom] = useState(defaultEffectiveFrom);
  const [startDate, setStartDate] = useState(
    parseDateString(billingItem?.startDate)
  );
  const [endDate, setEndDate] = useState(parseDateString(billingItem?.endDate));
  const [billingPeriod, setBillingPeriod] = useState(
    billingItem?.billingPeriod ?? BillingPeriod.MONTHLY
  );
  const [isDemo, setIsDemo] = useState(!!billingItem?.isDemo);
  const [segments, setSegments] = useState<EditableSegment[]>(
    billingItem ? toEditableSegments(billingItem, initialQuantity) : []
  );
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<Error | null>(null);

  const {
    mutate: setQuantity,
    isPending: isSavingQuantity,
    error: setQuantityError,
  } = useSetBillingItemQuantityMutation(storeId ?? null);
  const {
    mutate: updateBillingItem,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateBillingItemMutation(storeId ?? null);

  const billingQuantity = Math.max(0, displayQuantity - quantityOffset);
  const quantityOnEffectiveFrom = getQuantityOnDate(
    billingItems ?? [],
    availableBillingItemId,
    effectiveFrom
  );
  const activeSince = billingItem
    ? getActiveSinceOnDate(billingItem, effectiveFrom)
    : null;
  const minimumEffectiveFrom =
    billingItem && activeSince
      ? getMinimumActiveUntilDate(billingItem.type, activeSince)
      : null;
  const endDateActiveSince = billingItem
    ? getActiveSinceOnDate(billingItem, endDate || billingItem.startDate)
    : null;
  const minimumEndDate =
    billingItem && endDateActiveSince
      ? getMinimumActiveUntilDate(billingItem.type, endDateActiveSince)
      : null;
  const isReducingQuantity = billingQuantity < quantityOnEffectiveFrom;
  const effectiveFromBeforeMinimum =
    !!billingItem &&
    !!activeSince &&
    isReducingQuantity &&
    isDateBeforeMinimumActiveUntil(
      billingItem.type,
      activeSince,
      effectiveFrom
    );
  const endDateBeforeMinimum =
    !!billingItem &&
    !!endDateActiveSince &&
    isDateBeforeMinimumActiveUntil(
      billingItem.type,
      endDateActiveSince,
      endDate
    );
  const paidUntil = billingItem ? parseDateString(billingItem.paidUntil) : "";
  const nonAdminMinEndDate = getNonAdminMinimumEndDate(paidUntil, todayIso);
  const paidUntilLimitsEndDate =
    !isAdmin && !!paidUntil && paidUntil > todayIso;
  const endDateBeforePaidUntil =
    !isAdmin && isEndDateBeforePaidUntil(endDate, paidUntil);
  const timeline = getQuantityTimeline(
    billingItems ?? [],
    availableBillingItemId
  );
  const isSaving = isSavingQuantity || isUpdating;
  const editSegments = isAdmin && !!billingItem;

  const updateSegment = (
    index: number,
    patch: Partial<EditableSegment>
  ) => {
    setSegments((current) =>
      current.map((segment, segmentIndex) =>
        segmentIndex === index ? { ...segment, ...patch } : segment
      )
    );
  };

  const addSegment = () => {
    const lastSegment = segments[segments.length - 1];
    const nextStartDate = lastSegment?.startDate
      ? DateTime.fromISO(lastSegment.startDate).plus({ days: 1 }).toISODate() ??
        today
      : today;
    setSegments((current) => [
      ...current,
      {
        startDate: nextStartDate,
        quantity: lastSegment?.quantity ?? billingQuantity,
        billingPeriod: lastSegment?.billingPeriod ?? BillingPeriod.MONTHLY,
        paidUntil: "",
        discountAmount: lastSegment?.discountAmount ?? 0,
      },
    ]);
  };

  const removeSegment = (index: number) => {
    if (segments.length <= 1) {
      return;
    }
    setSegments((current) =>
      current.filter((_, segmentIndex) => segmentIndex !== index)
    );
  };

  const buildItemUpdate = (): RestUpdateBillingItem => {
    const update: RestUpdateBillingItem = {};
    if (!billingItem) {
      return update;
    }
    const firstSegmentStartDate = getFirstSegmentStartDate(billingItem);
    if (
      canEditStartDateAndPeriod &&
      startDate &&
      (startDate !== parseDateString(billingItem.startDate) ||
        (firstSegmentStartDate !== undefined &&
          startDate !== parseDateString(firstSegmentStartDate)))
    ) {
      update.startDate = startDate;
    }
    if (endDate !== parseDateString(billingItem.endDate)) {
      if (isAdmin || !endDate || endDate >= todayIso) {
        if (!isAdmin && isEndDateBeforePaidUntil(endDate, paidUntil)) {
          return update;
        }
        update.endDate = endDate || null;
      }
    }
    if (
      canEditStartDateAndPeriod &&
      billingPeriod !== (billingItem.billingPeriod ?? BillingPeriod.MONTHLY)
    ) {
      update.billingPeriod = billingPeriod;
    }
    return withSyncedSegmentDates(billingItem, update);
  };

  const hasResellerChanges = () => {
    const itemUpdate = buildItemUpdate();
    const invalidPastEndDate =
      !isAdmin &&
      !!endDate &&
      endDate < todayIso &&
      endDate !== parseDateString(billingItem?.endDate);
    const invalidPaidUntilEndDate =
      !isAdmin &&
      isEndDateBeforePaidUntil(endDate, paidUntil) &&
      endDate !== parseDateString(billingItem?.endDate);
    return (
      billingQuantity !== quantityOnEffectiveFrom ||
      Object.keys(itemUpdate).length > 0 ||
      invalidPastEndDate ||
      invalidPaidUntilEndDate
    );
  };

  const hasAdminChanges = () => {
    if (!billingItem) {
      return billingQuantity !== quantityOnEffectiveFrom;
    }
    const initialSegments = JSON.stringify(
      toEditableSegments(billingItem, initialQuantity)
    );
    const currentSegments = JSON.stringify(segments);
    return (
      isDemo !== !!billingItem.isDemo ||
      endDate !== parseDateString(billingItem.endDate) ||
      currentSegments !== initialSegments
    );
  };

  const saveAdminSegments = () => {
    if (!billingItem) {
      return;
    }
    const startDates = segments.map((segment) => segment.startDate);
    if (
      startDates.some((date) => !date) ||
      new Set(startDates).size !== startDates.length
    ) {
      setSegmentError(t("billingItems.edit.duplicateSegmentDates"));
      return;
    }
    const apiSegments = toApiSegments(segments, storeCurrency);
    const firstSegment = apiSegments[0];
    const lastActiveSegment = [...apiSegments]
      .reverse()
      .find((segment) => segment.quantity > 0);
    if (!firstSegment) {
      return;
    }
    setSegmentError(null);
    const update = withSyncedSegmentDates(billingItem, {
      segments: apiSegments,
      endDate: endDate || null,
      isDemo,
      paidUntil: lastActiveSegment?.paidUntil ?? null,
      billingPeriod: lastActiveSegment?.billingPeriod ?? null,
      discount: lastActiveSegment?.discount ?? null,
    });
    updateBillingItem(
      { billingItemId: billingItem.id, billingItem: update },
      { onSuccess: onClose }
    );
  };

  const saveResellerOrSimple = (from: string) => {
    const itemUpdate = buildItemUpdate();
    const quantityChanged = billingQuantity !== quantityOnEffectiveFrom;
    if (quantityChanged) {
      setQuantity(
        {
          availableBillingItemId,
          quantity: billingQuantity,
          effectiveFrom: from,
        },
        {
          onSuccess: () => {
            if (!billingItem) {
              onClose();
              return;
            }
            const dateUpdate = { ...itemUpdate };
            delete dateUpdate.segments;
            if (Object.keys(dateUpdate).length > 0) {
              updateBillingItem(
                { billingItemId: billingItem.id, billingItem: dateUpdate },
                { onSuccess: onClose }
              );
              return;
            }
            onClose();
          },
        }
      );
      return;
    }
    if (billingItem && Object.keys(itemUpdate).length > 0) {
      updateBillingItem(
        { billingItemId: billingItem.id, billingItem: itemUpdate },
        { onSuccess: onClose }
      );
      return;
    }
    onClose();
  };

  const handleSave = () => {
    if (editSegments) {
      saveAdminSegments();
      return;
    }
    if (
      !isAdmin &&
      endDate &&
      endDate < todayIso &&
      endDate !== parseDateString(billingItem?.endDate)
    ) {
      setSegmentError(t("billingItems.edit.endDateInPast"));
      return;
    }
    if (
      !isAdmin &&
      isEndDateBeforePaidUntil(endDate, paidUntil) &&
      endDate !== parseDateString(billingItem?.endDate)
    ) {
      setSegmentError(
        t("billingItems.edit.endDateBeforePaidUntil", {
          paidUntilDate: formatDate(paidUntil),
        })
      );
      return;
    }
    setSegmentError(null);
    const from =
      isAdmin || effectiveFrom >= todayIso ? effectiveFrom : todayIso;
    if (from !== effectiveFrom) {
      setEffectiveFrom(from);
    }
    saveResellerOrSimple(from);
  };

  const hasChanges = editSegments ? hasAdminChanges() : hasResellerChanges();

  return (
    <Modal isOpen onClose={onClose} title={title}>
      <EditorFields>
        {infoMessage && <InfoBox variant="info">{infoMessage}</InfoBox>}

        {editSegments ? (
          <>
            <FormGroup>
              <FormLabel>{t("billingItems.edit.isDemo")}</FormLabel>
              <Toggle checked={isDemo} onChange={setIsDemo} />
            </FormGroup>
            <FormGroup>
              <FormLabel>{t("billingItems.table.endDate")}</FormLabel>
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </FormGroup>
            {endDateBeforeMinimum && minimumEndDate && (
              <InfoBox variant="warning">
                {t("billingItems.edit.endDateBeforeMinimum", {
                  minimumDate: formatDate(minimumEndDate),
                })}
              </InfoBox>
            )}
            <FormLabel>{t("billingItems.edit.segments")}</FormLabel>
            <SegmentList>
              {segments.map((segment, index) => (
                <SegmentCard key={`${segment.startDate}-${index}`}>
                  <SegmentHeader>
                    <SegmentTitle>
                      {formatDate(segment.startDate) ||
                        t("billingItems.edit.segments")}
                    </SegmentTitle>
                    {segments.length > 1 && (
                      <Button
                        type="button"
                        $color="secondary"
                        $small
                        onClick={() => removeSegment(index)}
                      >
                        {t("billingItems.edit.removeSegment")}
                      </Button>
                    )}
                  </SegmentHeader>
                  <EditorRow>
                    <FormGroup>
                      <FormLabel>{t("billingItems.table.startDate")}</FormLabel>
                      <DateInput
                        type="date"
                        value={segment.startDate}
                        onChange={(e) =>
                          updateSegment(index, { startDate: e.target.value })
                        }
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>{quantityLabel}</FormLabel>
                      <NumberInput
                        type="number"
                        min={minDisplayQuantity}
                        value={segment.quantity + quantityOffset}
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value, 10);
                          const nextDisplay = isNaN(parsed)
                            ? minDisplayQuantity
                            : Math.max(minDisplayQuantity, parsed);
                          updateSegment(index, {
                            quantity: Math.max(0, nextDisplay - quantityOffset),
                          });
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>
                        {t("billingItems.table.invoicePeriod")}
                      </FormLabel>
                      <SelectInput
                        value={segment.billingPeriod}
                        onChange={(e) =>
                          updateSegment(index, {
                            billingPeriod: e.target.value as BillingPeriod,
                          })
                        }
                      >
                        {Object.values(BillingPeriod).map((period) => (
                          <option key={period} value={period}>
                            {t(`billingItems.billingPeriod.${period}`)}
                          </option>
                        ))}
                      </SelectInput>
                    </FormGroup>
                  </EditorRow>
                  <EditorRow>
                    <FormGroup>
                      <FormLabel>{t("billingItems.edit.paidUntil")}</FormLabel>
                      <DateInput
                        type="date"
                        value={segment.paidUntil}
                        onChange={(e) =>
                          updateSegment(index, { paidUntil: e.target.value })
                        }
                      />
                    </FormGroup>
                    <MoneyInput
                      label={t("billingItems.edit.discountAmount")}
                      amount={segment.discountAmount}
                      currency={storeCurrency}
                      onAmountChange={(amount) =>
                        updateSegment(index, {
                          discountAmount: Number.isFinite(amount) ? amount : 0,
                        })
                      }
                      placeholder="0"
                    />
                  </EditorRow>
                </SegmentCard>
              ))}
            </SegmentList>
            <Button type="button" $color="secondary" onClick={addSegment}>
              {t("billingItems.edit.addSegment")}
            </Button>
          </>
        ) : (
          <>
            <EditorRow>
              <FormGroup>
                <FormLabel>{quantityLabel}</FormLabel>
                <NumberInput
                  type="number"
                  min={minDisplayQuantity}
                  value={displayQuantity}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    setDisplayQuantity(
                      isNaN(parsed)
                        ? minDisplayQuantity
                        : Math.max(minDisplayQuantity, parsed)
                    );
                  }}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>{t("packageSelection.effectiveFrom")}</FormLabel>
                <DateInput
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  min={isAdmin ? undefined : todayIso}
                />
              </FormGroup>
            </EditorRow>
            {effectiveFromBeforeMinimum && minimumEffectiveFrom && (
              <InfoBox variant="warning">
                {t("billingItems.edit.effectiveFromBeforeMinimum", {
                  minimumDate: formatDate(minimumEffectiveFrom),
                })}
              </InfoBox>
            )}
            {billingItem && (
              <>
                {!canEditStartDateAndPeriod && (
                  <DateLockedMessage>
                    {t("billingItems.edit.startDateLocked")}
                  </DateLockedMessage>
                )}
                <EditorRow>
                  <FormGroup>
                    <FormLabel>{t("billingItems.table.startDate")}</FormLabel>
                    <DateInput
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={isAdmin ? undefined : todayIso}
                      disabled={!canEditStartDateAndPeriod}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>{t("billingItems.table.endDate")}</FormLabel>
                    <DateInput
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={isAdmin ? undefined : nonAdminMinEndDate}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>
                      {t("billingItems.table.invoicePeriod")}
                    </FormLabel>
                    <SelectInput
                      value={billingPeriod}
                      onChange={(e) =>
                        setBillingPeriod(e.target.value as BillingPeriod)
                      }
                      disabled={!canEditStartDateAndPeriod}
                    >
                      {Object.values(BillingPeriod).map((period) => (
                        <option key={period} value={period}>
                          {t(`billingItems.billingPeriod.${period}`)}
                        </option>
                      ))}
                    </SelectInput>
                  </FormGroup>
                </EditorRow>
                {paidUntilLimitsEndDate && (
                  <InfoBox variant="info">
                    {t("billingItems.edit.endDatePaidUntilLimit", {
                      paidUntilDate: formatDate(paidUntil),
                    })}
                  </InfoBox>
                )}
                {endDateBeforePaidUntil && (
                  <InfoBox variant="error">
                    {t("billingItems.edit.endDateBeforePaidUntil", {
                      paidUntilDate: formatDate(paidUntil),
                    })}
                  </InfoBox>
                )}
                {endDateBeforeMinimum && minimumEndDate && (
                  <InfoBox variant="warning">
                    {t("billingItems.edit.endDateBeforeMinimum", {
                      minimumDate: formatDate(minimumEndDate),
                    })}
                  </InfoBox>
                )}
              </>
            )}
          </>
        )}

        {renderPreview && (
          <PreviewBox>
            {previewTitle && <PreviewTitle>{previewTitle}</PreviewTitle>}
            {renderPreview(
              editSegments
                ? segments[segments.length - 1]?.quantity ?? billingQuantity
                : billingQuantity
            )}
          </PreviewBox>
        )}

        {(segmentError || setQuantityError || updateError || cancelError) && (
          <InfoBox variant="error">
            {segmentError ??
              (setQuantityError || updateError || cancelError)?.message}
          </InfoBox>
        )}

        {!editSegments && timeline.length > 0 && (
          <>
            <FormLabel>
              {timelineLabel ?? t("packageSelection.terminalQuantityHistory")}
            </FormLabel>
            <TimelineList>
              {timeline.map((entry, index) => {
                const nextEntry = timeline[index + 1];
                return (
                  <TimelineEntry key={entry.startDate}>
                    {t("packageSelection.quantityFromUntil", {
                      quantity: formatTimelineQuantity(entry.quantity),
                      from: formatDate(entry.startDate),
                      until: nextEntry
                        ? formatDate(dayBefore(nextEntry.startDate))
                        : t("billingItems.timeline.untilFurtherNotice"),
                    })}
                  </TimelineEntry>
                );
              })}
            </TimelineList>
          </>
        )}
      </EditorFields>

      {billingItem && (
        <BillingItemEndButton
          availableBillingItemId={availableBillingItemId}
          billingItemType={billingItem.type}
          activeSince={getActiveSinceOnDate(billingItem, todayIso)}
          paidUntil={paidUntil || null}
          isAdmin={isAdmin}
          onSuccess={onClose}
          onError={setCancelError}
          disabled={isSaving}
        />
      )}

      <FormActions>
        <Button type="button" $color="secondary" onClick={onClose} disabled={isSaving}>
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          disabled={isSaving || !hasChanges}
          onClick={handleSave}
        >
          {isSaving
            ? t("packageSelection.savingQuantity")
            : t("common.save")}
        </Button>
      </FormActions>
    </Modal>
  );
};
